/**
 * GCP Icons Registry
 *
 * Provides official Google Cloud Platform service icons from local SVG assets.
 * Uses explicit imports with SVGR for optimal tree-shaking.
 */

import type { ComponentType, ReactNode } from 'react';

// Explicit SVG imports with SVGR
import GcpBigQuery from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-bigquery.svg?react';
import GcpCloudArmor from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-cloud-armor.svg?react';
import GcpCloudBuild from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-cloud-build.svg?react';
import GcpCloudFunctions from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-cloud-functions.svg?react';
import GcpCloudRun from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-cloud-run.svg?react';
import GcpCloudSQL from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-cloud-sql.svg?react';
import GcpCloudStorage from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-cloud-storage.svg?react';
import GcpComputeEngine from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-compute-engine.svg?react';
import GcpGeneric from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-generic.svg?react';
import GcpGKE from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-gke.svg?react';
import GcpIAM from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-iam.svg?react';
import GcpPubSub from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-pubsub.svg?react';
import GcpSecretManager from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-secret-manager.svg?react';
import GcpVertexAI from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-vertex-ai.svg?react';
import GcpVPC from '@/sections/attacks/attackPaths/components/icons/bundles/gcp/gcp-vpc.svg?react';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { GCP_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Component Type
// ============================================================================

type GcpIconComponent = ComponentType<{
  className?: string;
  width?: string | number;
  height?: string | number;
}>;

// ============================================================================
// Fallback Component
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from GCP_ICONS names to SVGR components.
 */
const gcpIconComponents: Partial<
  Record<(typeof GCP_ICONS)[number], GcpIconComponent>
> = {
  CloudFunctions: GcpCloudFunctions,
  CloudStorage: GcpCloudStorage,
  CloudSQL: GcpCloudSQL,
  BigQuery: GcpBigQuery,
  PubSub: GcpPubSub,
  GKE: GcpGKE,
  ComputeEngine: GcpComputeEngine,
  CloudRun: GcpCloudRun,
  IAM: GcpIAM,
  SecretManager: GcpSecretManager,
  VPC: GcpVPC,
  CloudArmor: GcpCloudArmor,
  CloudBuild: GcpCloudBuild,
  VertexAI: GcpVertexAI,
  Generic: GcpGeneric,
};

/**
 * Human-readable labels for GCP icons.
 */
const gcpIconLabels: Partial<Record<(typeof GCP_ICONS)[number], string>> = {
  CloudFunctions: 'GCP Cloud Functions',
  CloudStorage: 'GCP Cloud Storage',
  CloudSQL: 'GCP Cloud SQL',
  BigQuery: 'GCP BigQuery',
  PubSub: 'GCP Pub/Sub',
  GKE: 'GCP Kubernetes Engine',
  ComputeEngine: 'GCP Compute Engine',
  CloudRun: 'GCP Cloud Run',
  IAM: 'GCP IAM',
  SecretManager: 'GCP Secret Manager',
  VPC: 'GCP VPC',
  CloudArmor: 'GCP Cloud Armor',
  CloudBuild: 'GCP Cloud Build',
  VertexAI: 'GCP Vertex AI',
  Generic: 'GCP (Generic)',
};

// ============================================================================
// GCP Renderer Implementation
// ============================================================================

/**
 * GCP icon renderer for the attack graph icon system.
 */
const gcpRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent = gcpIconComponents[name as (typeof GCP_ICONS)[number]];
    if (!IconComponent) return null;
    return <IconComponent className={className} width={size} height={size} />;
  },

  validate: (name: string): boolean => {
    return (GCP_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent = gcpIconComponents[name as (typeof GCP_ICONS)[number]];
    if (!IconComponent) return null;

    return function WrappedGcpIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} width={size} height={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    GCP_ICONS.map(name => {
      const IconComponent = gcpIconComponents[name];
      return {
        id: `gcp:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: gcpIconLabels[name] ?? name,
        category: 'gcp',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('gcp', gcpRenderer);

export { gcpRenderer };
